import { GetXcoreStaticContext, useContentSingletons, usePageContent, useXcoreUtils } from "@robe/client/xcore-ui";
import { LayoutNoHead } from "components/Layout";
import RobeHead from "components/RobeHead";
import { useRouter } from "next/router";
import ProductOverview from "components/ProductOverview";
import { useTagManager } from "hooks/useTagManager";
import { getRobePaths, getRobeProps, RobePage } from "xcore";
import { ProductOverviewPage as ProductOverviewPageType, Products } from "xcore/types";
import { fetchRelationsForSingleContent } from "@robe/core";

export const getXcoreOptions = ({ core, id }: GetXcoreStaticContext) => {
  const productsContentType = core.content
    .singletonByType<Products>("products", {
      fields: [
        "title",
        "intro",
        "middlePageMiddleFilter",
        "middlePageRightFilter",
        "middlePageContent",
        "featuredCategoriesHeading",
        "middlePageLeftFilter",
        "browseAllBtn",
        "leftLabelName",
        "leftPlaceholderText",
        "midLabelName",
        "midPlaceholderName",
        "rightLabelName",
        "rightPlaceholderText",
        "itemsRelation",
        "hideFilters",
        "hideDiscontinued",
      ],
    })
    .then(
      fetchRelationsForSingleContent({
        core,
        relationSourceFields: [
          { field: "itemsRelation" },
          { field: "middlePageLeftFilter" },
          { field: "middlePageMiddleFilter" },
          { field: "middlePageRightFilter" },
          { field: "middlePageContent" },
        ],
        options: {
          includeValues: true,
        },
      })
    );

  return {
    // PageContent is POP if POP id is provided, otherwise it is general products content type
    pageContent: id
      ? core.content
        .singleById<ProductOverviewPageType>(id, {
          includeValues: true,
        })
        .then(
          fetchRelationsForSingleContent({
            core,
            relationSourceFields: [{ field: "itemsRelation" }],
            options: {
              fields: [
                "heading",
                "images",
                "discontinuedGlob",
                "discontinuedReg",
                "name",
                "thumbnailTitle",
                "title",
                "thumbImage",
                "thumbImageProduct",
                "newIcon",
                "ipCertifiedIcon",
                "textTag",
              ],
            },
          })
        )
      : productsContentType,

    // Products content type is assigned to contentSingletons in both cases - whether we are on /products page or POP
    contentSingletons: [productsContentType],
    subCategories: [
      core.subCategory.listByTopLevelCategory<ProductOverviewPageType>("product-type", { includeContents: true }),
      core.subCategory.listByTopLevelCategory<ProductOverviewPageType>("product-size", { includeContents: true }),
      core.subCategory.listByTopLevelCategory<ProductOverviewPageType>("beam-type", { includeContents: true }),
    ],
  };
};

export const getStaticProps = getRobeProps(getXcoreOptions);

export const getStaticPaths = getRobePaths("productOverviewPage");

const ProductOverviewPage: RobePage = () => {
  const { stringify, richtextToString } = useXcoreUtils();
  const pop = usePageContent<ProductOverviewPageType>();
  const [products] = useContentSingletons<[Products]>();
  const { query } = useRouter();
  const isProductDetail = !!query.id; // differentiate overview index and overview detail pages for different title purposes

  useTagManager("product-listing");

  const popTitle = stringify(products?.values.title);
  return (
    <>
      <RobeHead
        title={isProductDetail ? [stringify(pop?.title), popTitle] : [stringify(pop?.title)]}
        description={isProductDetail ? richtextToString(products?.values.intro) : richtextToString(pop.values.intro)}
      />

      <ProductOverview />
    </>
  );
};

ProductOverviewPage.Layout = LayoutNoHead;

export default ProductOverviewPage;
